import { Box } from '@mui/material';
import { DateField, FunctionField, useTranslate } from 'react-admin';

import { NginxLogEntry } from 'types/LiveLogs';
import { BinarySizeField } from '../BinarySizeField';
import { LiveLogDatagrid } from './LiveLogDatagrid';
import { HIDE_BELOW_SM, LIVE_LOGS_I18N, liveLogColumnLabel, LOG_DATE_TIME_FORMAT } from './liveLogs';
import { singleLineMonoSx, tabularSx, twoLineMonoSx } from './logCellSx';
import { httpStatusColor, LogChip, logLevelColor } from './LogChip';

/** The HTTP code on access rows, the level on error rows. */
const renderStatus = (record: NginxLogEntry) => {
  if (record.status != null) {
    return <LogChip label={String(record.status)} color={httpStatusColor(record.status)} />;
  }
  if (record.level) {
    return <LogChip label={record.level} color={logLevelColor(record.level)} />;
  }
  return '-';
};

/** "GET /url" on one line for access rows, the message on at most two lines for error rows. */
const renderRequest = (record: NginxLogEntry) => {
  if (record.dataset === 'error' || (record.url == null && record.message != null)) {
    return record.message ? (
      <Box component="span" sx={twoLineMonoSx}>
        {record.message}
      </Box>
    ) : (
      '-'
    );
  }

  const request = [record.method, record.url].filter(Boolean).join(' ');
  return request ? (
    <Box component="span" title={request} sx={singleLineMonoSx}>
      {request}
    </Box>
  ) : (
    '-'
  );
};

export const NginxLogGrid = () => {
  const translate = useTranslate();

  return (
    <LiveLogDatagrid>
      <DateField<NginxLogEntry>
        source="timestamp"
        emptyText="-"
        label={liveLogColumnLabel('time')}
        sortable={false}
        showTime
        options={LOG_DATE_TIME_FORMAT}
        sx={tabularSx}
      />
      <FunctionField<NginxLogEntry>
        label={liveLogColumnLabel('type')}
        sortable={false}
        cellClassName={HIDE_BELOW_SM}
        headerClassName={HIDE_BELOW_SM}
        render={(record) => (record.dataset ? translate(`${LIVE_LOGS_I18N}.datasets.${record.dataset}`) : '-')}
      />
      <FunctionField<NginxLogEntry> label={liveLogColumnLabel('status')} sortable={false} render={renderStatus} />
      <FunctionField<NginxLogEntry> label={liveLogColumnLabel('request')} sortable={false} render={renderRequest} />
      <FunctionField<NginxLogEntry>
        label={liveLogColumnLabel('size')}
        sortable={false}
        textAlign="right"
        cellClassName={HIDE_BELOW_SM}
        headerClassName={HIDE_BELOW_SM}
        sx={tabularSx}
        render={() => <BinarySizeField source="bytes" />}
      />
    </LiveLogDatagrid>
  );
};
